<template>
  <div :key="refreshKey" class="flex flex-col gap-5 dark:text-gray-200">
    <VideoDropdownModal v-model:open="showVideoModal" :video-url="videoURL" />
    <!-- Header Section -->
    <div
      class="relative flex flex-col w-full h-min text-main-700 bg-white rounded-lg bg-clip-border p-4 gap-5 dark:bg-gray-800"
    >
      <nav class="w-full flex flex-col md:flex-row justify-between gap-5">
        <!-- Back Button and Title -->
        <div
          class="w-full max-w-fit flex flex-row gap-5 items-center justify-start"
        >
          <button
            @click="goback"
            class="px-4 flex flex-col items-center justify-center aspect-square w-fit bg-blue-gray-500/10 hover:bg-blue-gray-500/20 border-bluegray-600/10 rounded-lg dark:bg-gray-700 dark:hover:bg-gray-600"
            aria-label="Go back"
          >
            <i class="fa-solid fa-arrow-left text-base dark:text-gray-200"></i>
          </button>
          <h3
            class="text-[clamp(18px,2vw,24px)] font-medium dark:text-gray-200"
          >
            Order History
          </h3>
        </div>

        <!-- Date Picker and Export Button -->
        <div
          class="flex flex-col md:flex-row justify-end items-center gap-4 w-full"
        >
          <!-- Date Picker -->
          <div class="w-full md:max-w-sm min-w-[200px] relative">
            <div class="relative h-10 w-full min-w-[100px]">
              <div
                class="absolute grid w-5 h-5 top-2/4 right-3 -translate-y-2/4 place-items-center text-main-500 dark:text-gray-400"
              >
                <i class="fa-regular fa-calendar"></i>
              </div>
              <flat-pickr
                v-model="dateRange"
                :config="flatpickrOptions"
                class="peer pl-3 outline outline-0 focus:outline-0 pr-11 dark:bg-gray-700 dark:placeholder-gray-400 py-3 h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline-none transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-500 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-500 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700"
                placeholder="Select a date range"
              />
            </div>
          </div>

          <!-- Export Button -->
          <button
            class="flex items-center gap-3 w-full md:w-auto align-middle justify-center select-none font-semibold text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-sm md:text-base py-2 px-4 rounded-lg bg-button text-white shadow-md shadow-gray-900/10 hover:shadow-lg hover:shadow-main-200 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none dark:bg-sub-900 dark:hover:bg-gray-500"
            type="button"
            aria-label="Export"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="2"
              stroke="currentColor"
              aria-hidden="true"
              class="w-4 h-4"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
              ></path>
            </svg>
            Export
          </button>
        </div>
      </nav>
    </div>

    <!-- Loading Indicator -->
    <div
      v-if="loading"
      class="w-full flex flex-col justify-center items-center h-20 mt-6 mb-16"
    >
      <LoadingComponent />
    </div>

    <!-- Table Section -->
    <div
      :key="refreshKey"
      v-else
      class="relative flex flex-col w-full h-full text-gray-700 bg-white shadow-md rounded-lg bg-clip-border overflow-auto dark:bg-gray-800 dark:text-gray-200"
    >
      <table class="w-full text-left table-auto">
        <thead>
          <tr class="p-4 bg-blue-gray-50/50 dark:bg-gray-700">
            <!-- Checkbox Column -->
            <th class="p-4 text-center">
              <label
                class="relative flex justify-center items-center rounded-full cursor-pointer"
                htmlFor="link"
              >
                <input
                  type="checkbox"
                  class="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-main-400 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-main-500 before:opacity-0 before:transition-opacity checked:border-main-900 checked:bg-main-900 checked:before:bg-main-900 hover:before:opacity-10 dark:border-gray-500 dark:checked:border-gray-200 dark:checked:bg-gray-200"
                  id="link"
                />
                <span
                  class="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100 dark:text-gray-800"
                >
                  <i class="fa-solid fa-check"></i>
                </span>
              </label>
            </th>

            <!-- Table Headers -->
            <th
              class="text-center text-sm md:text-base font-medium leading-none text-gray-800 dark:text-gray-200"
            >
              Order ID
            </th>
            <th
              class="text-center text-sm md:text-base font-medium leading-none text-gray-800 dark:text-gray-200"
            >
              Amount
            </th>
            <th
              class="text-center text-sm md:text-base font-medium leading-none text-gray-800 dark:text-gray-200"
            >
              Date
            </th>
          </tr>
        </thead>

        <tbody>
          <!-- No Orders Message -->
          <tr v-if="!loading && orders.length === 0">
            <td colspan="6" class="py-12 px-4">
              <div class="max-w-xs mx-auto text-center">
                <div
                  class="mx-auto w-24 h-24 mb-4 text-gray-400 dark:text-gray-500"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m6 4.125l2.25 2.25m0 0l2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
                    />
                  </svg>
                </div>
                <h3
                  class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1"
                >
                  No orders yet
                </h3>
                <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  When you receive new orders, they'll appear here.
                </p>
                <div class="flex justify-center gap-3">
                  <button
                    @click="fetchOrders"
                    class="inline-flex items-center px-3.5 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-main-600 dark:bg-sub-800 hover:bg-main-700 dark:hover:bg-sub-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-main-500 dark:focus:ring-sub-600"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="-ml-0.5 mr-2 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    Refresh
                  </button>
                  <router-link
                    to="/pos"
                    class="inline-flex items-center px-3.5 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-main-500"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="-ml-0.5 mr-2 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    Create Order
                  </router-link>
                </div>
              </div>
            </td>
          </tr>

          <!-- Orders List -->
          <tr
            v-for="order in orders"
            :key="order.id"
            class="border-b border-main-50 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
          >
            <!-- Checkbox -->
            <td
              class="p-4 border-b border-blue-gray-50 py-5 text-center dark:border-gray-600"
            >
              <label
                class="relative flex justify-center items-center rounded-full cursor-pointer"
                :for="`checkbox-${order.id}`"
              >
                <input
                  type="checkbox"
                  class="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-main-400 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-main-500 before:opacity-0 before:transition-opacity checked:border-main-900 checked:bg-main-900 checked:before:bg-main-900 hover:before:opacity-10 dark:border-gray-500 dark:checked:border-gray-200 dark:checked:bg-gray-200"
                  :id="`checkbox-${order.id}`"
                  v-model="selectedOrders"
                  :value="order.id"
                />
                <span
                  class="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100 dark:text-gray-800"
                >
                  <i class="fa-solid fa-check"></i>
                </span>
              </label>
            </td>

            <!-- Order Details -->
            <td
              class="p-4 border-b border-blue-gray-50 py-5 text-center max-w-[200px] dark:border-gray-600"
            >
              <router-link
                :to="`/order/${order.id}`"
                class="flex items-center justify-center gap-3 underline md:no-underline md:hover:underline"
              >
                <p
                  class="block text-sm antialiased font-bold leading-normal text-center text-main-900 dark:text-gray-200"
                >
                  {{ order.orderId }}
                </p>
              </router-link>
            </td>
            <td
              class="p-4 border-b border-blue-gray-50 py-5 text-center max-w-[200px] dark:border-gray-600"
            >
              <p
                class="block text-sm antialiased font-normal leading-normal text-main-900 dark:text-gray-200"
              >
                {{ getCurrencySymbol(storeCurrencyId)
                }}{{ parseFloat(order.amount).toLocaleString() }}
              </p>
            </td>
            <td
              class="p-4 border-b border-blue-gray-50 py-5 text-center max-w-[200px] dark:border-gray-600"
            >
              <p
                class="block text-sm antialiased font-normal leading-normal text-main-900 dark:text-gray-200"
              >
                {{
                  new Date(order.orderDate).toLocaleDateString("en-US", {
                    weekday: "short",
                    hour: "numeric",
                    minute: "numeric",
                  })
                }}
              </p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { mapActions } from "vuex";
import flatpickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import eventBus from "@/utils/eventBus";

export default {
  name: "OrderHistory",
  components: {
    FlatPickr: flatpickr,
  },
  data() {
    return {
      dateRange: [],
      flatpickrOptions: {
        mode: "range",
        dateFormat: "d-m-Y",
        wrap: true,
        weekNumbers: true,
      },
      selectAll: false,
      selectedOrders: [],
      orders: [],
      currencies: [],
      loading: true,
      showVideoModal: false,
      videoURL: "https://www.youtube.com/watch?v=mJZA8DxE97U",
      refreshKey: this.$store.state.storeUpdatedAt || Date.now(),
    };
  },
  mounted() {
    this.getCurrency();
    eventBus.on("open-help-video", this.handleOpenVideo);
  },
  provide() {
    return {
      helpVideoURL: this.videoURL,
    };
  },
  beforeUnmount() {
    eventBus.off("open-help-video", this.handleOpenVideo);
  },
  computed: {
    storeCurrencyId() {
      return this.$store.state.selectedStore.default_currency;
    },
  },
  methods: {
    ...mapActions(["getAllOrders", "fetchCurrency"]),
    handleOpenVideo() {
      this.showVideoModal = true;
    },
    goback() {
      this.$router.back();
    },
    getCurrencySymbol(id) {
      if (id) {
        const currency = this.currencies.find((currency) => currency.id === id);
        return currency ? currency.currency_symbol : null;
      }
    },
    getCurrencyCode(id) {
      if (id) {
        const currency = this.currencies.find((currency) => currency.id === id);
        return currency ? currency.currency_code : null;
      }
    },
    async getCurrency() {
      const response = await this.fetchCurrency();
      if (response.success) {
        this.currencies = response.currency;
      }
    },
    async fetchOrders() {
      this.loading = true;
      try {
        const response = await this.getAllOrders();
        if (response.success) {
          this.orders = response.orders;
        } else {
          toast.error(response.error || "Failed to fetch orders");
        }
      } catch (error) {
        toast.error("Error fetching orders");
      } finally {
        this.loading = false;
      }
    },
    toggleSelectAll() {
      this.selectAll = !this.selectAll;
      if (this.selectAll) {
        this.selectedOrders = this.orders.map((order) => order.id);
      } else {
        this.selectedOrders = [];
      }
    },
  },
  watch: {
    storeOrders(newOrders) {
      this.orders = newOrders;
    },
    "$store.state.storeUpdatedAt": {
      immediate: true,
      handler(val) {
        this.refreshKey = val;
        this.fetchOrders();
      },
    },
    dateRange(newDates) {
      console.log("Date Range Updated:", newDates);
    },
  },
  created() {
    this.fetchOrders();
  },
};
</script>
